import { useState } from "react";
import { useDispatch } from "react-redux";
import PropTypes from "prop-types";
import DOMPurify from "dompurify";
import { Modal } from "@/config/antd";
import { CloseOutlined } from "@/config/antd";
import Button from "@/components/common/Button";
import TextEditor from "@/components/common/inputs/Text_Editor";
import FileInput from "@/components/common/inputs/File_Input";
import { useLoader } from "@/hooks/LoaderProvider";
import Success from "@/components/common/alerts/Success";
import { replySupportTicketAsync } from "@/store/redux/slices/support/support_slice";

const ReplyTicketModal = ({
  isVisible,
  onClose,
  recipientName,
  supportTicketID,
  onReplySent,
}) => {
  const dispatch = useDispatch();
  const { showLoader, hideLoader } = useLoader();

  const [replyData, setReplyData] = useState({
    message: "",
    attachment: null,
  });
  const [isSuccessVisible, setIsSuccessVisible] = useState(false);

  const handleSendClick = async () => {
    if (!supportTicketID || !replyData.message?.trim()) {
      return;
    }

    onClose();
    showLoader();

    try {
      const sanitizedMessage = DOMPurify.sanitize(replyData.message, {
        ALLOWED_TAGS: [],
        ALLOWED_ATTR: [],
      }).trim();

      const replyPayload = {
        supportTicketID,
        description: sanitizedMessage,
      };
      console.log("Reply ticket payload (modal):", replyPayload);

      await dispatch(replySupportTicketAsync(replyPayload)).unwrap();

      onReplySent?.(sanitizedMessage);

      setReplyData({
        message: "",
        attachment: null,
      });

      setIsSuccessVisible(true);
    } catch (err) {
      console.error("Failed to send reply:", err);
    } finally {
      hideLoader();
    }
  };

  const handleSuccessClose = () => {
    setIsSuccessVisible(false);
  };

  const handleFileChange = (e) => {
    setReplyData((prev) => ({
      ...prev,
      attachment: e.target.files[0],
    }));
  };

  return (
    <>
      <Modal
        open={isVisible}
        onCancel={onClose}
        footer={null}
        centered
        width={500}
        closeIcon={null}
        styles={{
          body: { padding: 0 },
          content: {
            padding: 0,
            borderRadius: "16px",
            overflow: "hidden",
          },
          mask: { backgroundColor: "rgba(0, 0, 0, 0.75)" },
        }}>
        <div className="flex flex-col bg-white">
          <div className="p-4 flex justify-between items-center border-b border-gray-200">
            <h2 className="text-bold-x text-primary">Reply Ticket Message</h2>
            <CloseOutlined
              onClick={onClose}
              className="cursor-pointer"
              style={{
                color: "var(--veegil-color-primary)",
                backgroundColor: "var(--veegil-color-secondary)",
                borderRadius: "50%",
                padding: "4px",
              }}
            />
          </div>

          <div className="flex flex-col gap-4 p-6">
            <TextEditor
              label="MESSAGE"
              placeholder="Type your reply here..."
              value={replyData.message}
              onChange={(value) =>
                setReplyData((prev) => ({ ...prev, message: value }))
              }
            />

            <FileInput
              label="ATTACHMENT"
              placeholder="Upload attachment"
              id="reply-attachment"
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
              onChange={handleFileChange}
            />
          </div>

          <div className="w-full bg-secondary p-4 flex gap-3">
            <Button
              text="Cancel"
              color="secondary"
              className="flex-1 border border-primary"
              customBgColor="white"
              customTextColor="primary"
              onClick={onClose}
              fontWeight="normal"
            />
            <Button
              text="Send"
              color="primary"
              className="flex-1"
              customBgColor="primary"
              customTextColor="white"
              onClick={handleSendClick}
              fontWeight="normal"
            />
          </div>
        </div>
      </Modal>

      <Success
        isVisible={isSuccessVisible}
        onClose={handleSuccessClose}
        header="Message Sent"
        paragraph={`You will be alerted when ${
          recipientName || "the ticket owner"
        } replies to this message.`}
        buttonText="Close"
      />
    </>
  );
};

ReplyTicketModal.propTypes = {
  isVisible: PropTypes.bool,
  onClose: PropTypes.func,
  recipientName: PropTypes.string,
  supportTicketID: PropTypes.string,
  onReplySent: PropTypes.func,
};

export default ReplyTicketModal;
